import React, { useEffect, useState } from "react";
import * as appReportService from "../../../models/services/appReportService";
import { ISessionState } from "../../../models/session/sessionState";
import { ListDetailsPanel } from "source/components/framework/List/ListDetailsPanel";
import { AppUtils } from "source/framework/appUtils";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { Translations } from "ui/Scripts/source/models/translations";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { Alert, Box, Grid } from "@mui/material";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { ModifyUserInfo } from "source/components/framework/User/ModifyUserInfo";
import { ListDetailsPanelAccordion } from "source/components/framework/List/ListDetailsPanel/ListDetailsPanelAccordion";
import { FormFieldLockedToUser } from "source/components/framework/FormFieldLockedToUser";
import { IAppReportEditItem } from "ui/Scripts/source/models/appReport/appReportEditItem";
import { TaskRepetitionType, TaskVisibility } from "ui/Scripts/source/models/enums";
import { TaskTypeDialogPreviewPrint } from "source/components/task/TaskTypeDialog/TaskTypeDialogPreview/TaskTypeDialogPreviewPrint";
import { ISchemaParseResult, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { AppReportTemplateFormModel } from "ui/Scripts/source/models/appReportTemplate/appReportTemplateFormModel";
import { ChangeToolButton } from "source/components/framework/Button/ChangeToolButton";
import { DownloadToolButton } from "source/components/framework/Button/DownloadToolButton";

interface IAppReportListDetailsPanelProps {
    sessionState: ISessionState;
    companyId: string;
    appReportId: string;
    dataId: string;
    editDialogVisible: boolean;
    onPrintDraft: () => void;
    onPrintPublished: () => void;
    onClose: () => void;
}

interface IAppReportListDetailsPanelState {
    appReport: IAppReportEditItem;
    parseResult: ISchemaParseResult;
    unit: IIdTitleNode;
    isLockedToOtherUser: boolean;
    isPublished: boolean;
    canEdit: boolean;
    canChangePreviewDocument: boolean;
    showPublishedDocument: boolean;
    previewPrintIsLoading: boolean;
}

export const AppReportListDetailsPanel = (props: IAppReportListDetailsPanelProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState<IAppReportListDetailsPanelState>({
        appReport: null,
        parseResult: null,
        unit: null,
        isLockedToOtherUser: false,
        isPublished: false,
        canEdit: false,
        canChangePreviewDocument: false,
        showPublishedDocument: false,
        previewPrintIsLoading: false
    });

    const changeState = (obj: any) => {
        setState(prevState => ({
            ...prevState,
            ...obj
        }));
    };

    const loadAppReport = async () => {
        if (!props.appReportId) return;
        //Load appReport
        setIsLoading(true);
        const result = await AppUtils.callService(() => appReportService.getAppReportEdit(props.companyId, props.appReportId, false), false, true, () => setIsLoading(false));
        if (!result) return;
        const appReport = result.item;
        const parseResult = TaskEditTypeItem.getTaskTypeFromSchema(appReport.dataSchema);
        const isPublished = !!appReport.publishUser;
        AppReportTemplateFormModel.setTitlePageDataToFormDataRow(parseResult.taskType.formCols, parseResult.formDataRow, appReport.startDate, appReport.endDate);
        changeState({
            appReport,
            parseResult,
            unit: appReport.unitId ? IdTitleNode.getById(result.units, appReport.unitId) : null,
            isLockedToOtherUser: appReport.isLockedToOtherUser(props.sessionState.code),
            isPublished,
            canEdit: result.canEdit,
            canChangePreviewDocument: isPublished && result.canEdit,
            showPublishedDocument: isPublished
        });
        //Send read receipt for appReport
        if (!appReport.isRead) {
            const userIsCreator = appReport.createUser.code === props.sessionState.code;
            if (!userIsCreator) {
                AppUtils.callService(() => appReportService.sendAppReportReadReceipt(props.companyId, appReport.id), false, true);
            }
        }
    };

    useEffect(() => {
        if (props.editDialogVisible) return;
        loadAppReport();
    }, [props.companyId, props.appReportId, props.dataId, props.editDialogVisible]);
    
    const handleToggleShowPublishedDocument = async (e) => {
        e.stopPropagation();
        setState(prevState => ({
            ...prevState,
            showPublishedDocument: !prevState.showPublishedDocument
        }));
    };

    const handleDownload = async (e) => {
        e.stopPropagation();
        if (state.showPublishedDocument) {
            props.onPrintPublished();
            return;
        }
        props.onPrintDraft();
    };

    return (
        <ListDetailsPanel
            isLoading={isLoading}
            onClose={props.onClose}
        >
            {(!props.appReportId || (!state.appReport && isLoading)) &&
                <Alert severity="info">{Translations.SelectReportInListToSeeThePreview}</Alert>
            }
            {!!state.appReport &&
                <>
                    <Box style={{ padding: "8px 16px 16px" }}>
                        <GridRow>
                            <Grid item sm={6}>
                                <FormField title={Translations.TaskCreated} fullWidth margin={0}>
                                    <ModifyUserInfo
                                        modifyUser={state.appReport.createUser}
                                    />
                                </FormField>
                            </Grid>
                            {state.canEdit &&
                                <Grid item sm={6}>
                                    <FormField title={Translations.Modified} fullWidth margin={0}>
                                        <ModifyUserInfo
                                            modifyUser={state.appReport.modifyUser}
                                        />
                                    </FormField>
                                </Grid>
                            }
                            {state.isLockedToOtherUser && state.canEdit &&
                                <Grid item sm={6}>
                                    <FormFieldLockedToUser
                                        lockedUser={state.appReport.lockedUser}
                                    />
                                </Grid>
                            }
                            {state.appReport.publishUser &&
                                <Grid item sm={6}>
                                    <FormField title={Translations.Published} fullWidth margin={!state.canEdit ? 0 : undefined}>
                                        <ModifyUserInfo
                                            modifyUser={state.appReport.publishUser}
                                        />
                                    </FormField>
                                </Grid>
                            }
                            {state.appReport.archiveUser &&
                                <Grid item sm={6}>
                                    <FormField title={Translations.Archived} fullWidth>
                                        <ModifyUserInfo
                                            modifyUser={state.appReport.archiveUser}
                                        />
                                    </FormField>
                                </Grid>
                            }
                            {state.appReport.deleteUser &&
                                <Grid item sm={6}>
                                    <FormField title={Translations.MovedToTrashBin} fullWidth>
                                        <ModifyUserInfo
                                            modifyUser={state.appReport.deleteUser}
                                        />
                                    </FormField>
                                </Grid>
                            }
                        </GridRow>
                    </Box>
                    <ListDetailsPanelAccordion
                        title={Translations.Report}
                        subTitle={state.showPublishedDocument
                            ? "(" + Translations.PublishVersion + ")"
                            : "(" + Translations.DraftVersion + ")"
                        }
                        isLoading={state.previewPrintIsLoading}
                        buttons={<>
                            {state.canChangePreviewDocument &&
                                <ChangeToolButton
                                    onClick={handleToggleShowPublishedDocument}
                                    tooltip={state.showPublishedDocument
                                        ? Translations.ShowDraftVersion
                                        : Translations.ShowPublishVersion}
                                />
                            }
                            <DownloadToolButton onClick={handleDownload}/>
                        </>}
                    >
                        <>
                            {(state.showPublishedDocument
                                ? <Box dangerouslySetInnerHTML={{ __html: state.appReport.reportData }} />
                                : (state.parseResult?.taskType
                                    ? <TaskTypeDialogPreviewPrint
                                        companyId={props.companyId}
                                        code={state.appReport.code}
                                        taskRepetitionType={TaskRepetitionType.NotRepeated}
                                        dataSchema={state.appReport.dataSchema}
                                        printOrientationLandscape={false}
                                        visibility={TaskVisibility.Public}
                                        title={Translations.FormPrint}
                                        taskType={state.parseResult?.taskType}
                                        headerDataRow={null}
                                        formDataRow={state.parseResult?.formDataRow}
                                        headerTypeCol={null}
                                        formTypeCol={null}
                                        showPreviewWithoutCard={true}
                                        onGetPrintHtml={(obj) => {
                                            setState((old) => ({ ...old, previewPrintIsLoading: true }));
                                            return appReportService.getAppReportTemplatePreviewPrintHtml(obj);
                                        }}
                                        onGetPrintHtmlDisableLoadIndicator={true}
                                        onGetPrintHtmlFinally={() => {
                                            setState((old) => ({ ...old, previewPrintIsLoading: false }));
                                        }}
                                        onGetPrint={appReportService.getAppReportTemplatePreviewPrint}
                                        onHeaderItemClick={() => {}}
                                        onFormItemClick={() => {}}
                                    />
                                    : null

                                )
                            )}
                        </>
                    </ListDetailsPanelAccordion>
                </>
            }
        </ListDetailsPanel>
    );
};
